/**
 * The composer's templates (docs/SPEC.md §11.2, §11.3; P28-T10): "None" and
 * every template, each with a picture of its first scene, and the chosen
 * one playing under them. The pictures and the playing are the browser's
 * player over the template's bakes (`GET /v1/templates/{id}/manifest`): a
 * template's scenes are baked by the hourly tick, never stilled. A template
 * not baked yet shows its name and sentence over a grey picture.
 */
import { Suspense } from "react";

import type { FilmTemplate } from "../api/templates";
import { useManifest } from "../api/versions";
import { COMPOSER } from "../copy/brief";
import { frameSize } from "../film/frames";
import { LazyFilmPlayer } from "../film/lazy";
import { allReady, sceneAlone } from "../film/manifest";
import { useManifestFrame } from "../film/useFrame";
import { Skeleton } from "../ui";

/** An option's picture: 40 px wide, drawn at three times that. */
const OPTION_SHORT = 120;

function Option({
  template,
  checked,
  onSelect,
}: {
  template: FilmTemplate | null;
  checked: boolean;
  onSelect(): void;
}) {
  const frame = useManifestFrame(
    template?.manifest_url,
    null,
    frameSize(template?.aspect ?? "9:16", OPTION_SHORT),
    !!template?.manifest_url,
  );
  const name = template ? template.name : COMPOSER.templateNone;
  const sentence = template ? template.sentence : COMPOSER.templateNoneHint;
  return (
    <button
      type="button"
      role="radio"
      aria-checked={checked}
      aria-label={name}
      onClick={onSelect}
      className={[
        "flex items-start gap-3 rounded-md border p-2.5 text-left transition-colors",
        checked
          ? "border-brand bg-brand-tint/50 shadow-glow"
          : "border-slate-200 bg-white hover:border-brand-light",
      ].join(" ")}
      data-testid={`template-${template?.id ?? "none"}`}
    >
      <span className="block h-14 w-10 shrink-0 overflow-hidden rounded bg-slate-100">
        {frame ? (
          <img
            src={frame}
            alt=""
            className="h-full w-full object-cover"
            data-testid={`template-frame-${template?.id}`}
          />
        ) : null}
      </span>
      <span className="min-w-0">
        <span className="block text-sm font-semibold text-slate-900">
          {name}
        </span>
        <span className="block text-xs text-slate-500">{sentence}</span>
      </span>
    </button>
  );
}

/** The chosen template's scenes, playing: the whole film once every scene
 *  is baked, else its first baked scene alone. */
function Preview({ template }: { template: FilmTemplate }) {
  const manifest = useManifest(template.manifest_url);
  const data = manifest.data;
  if (!data) return null;
  const first = data.scenes.find((s) => s.ready);
  if (!first) return null;
  const shown = allReady(data) ? data : sceneAlone(data, first.id);
  return (
    <div
      className="mx-auto w-full max-w-sm"
      data-testid="template-preview"
      aria-label={template.name}
    >
      <Suspense fallback={<Skeleton height={240} />}>
        <LazyFilmPlayer
          key={template.id}
          manifest={shown}
          autoPlay
          maxHeight="min(45vh, 24rem)"
        />
      </Suspense>
    </div>
  );
}

export function TemplatePicker({
  templates,
  selected,
  onSelect,
}: {
  templates: FilmTemplate[];
  selected: string | null;
  onSelect: (id: string | null) => void;
}) {
  const chosen = templates.find((t) => t.id === selected) ?? null;
  return (
    <div className="flex flex-col gap-2" data-testid="templates">
      <span className="tb-field-label">{COMPOSER.template}</span>
      <div
        role="radiogroup"
        aria-label={COMPOSER.template}
        className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3"
      >
        <Option
          template={null}
          checked={selected === null}
          onSelect={() => onSelect(null)}
        />
        {templates.map((t) => (
          <Option
            key={t.id}
            template={t}
            checked={t.id === selected}
            onSelect={() => onSelect(t.id)}
          />
        ))}
      </div>
      {chosen?.manifest_url ? <Preview template={chosen} /> : null}
    </div>
  );
}
